import { useState, useRef, useCallback } from 'react';
import { ttsApi } from '@/services/api';

export function useOpenAITTS(language = 'fr') {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const currentAudioRef = useRef(null);

  const speak = useCallback(async (text, options = {}) => {
    if (!text || text.trim().length === 0) return;

    // Stop any current playback
    stop();

    setIsLoading(true);
    setError(null);

    try {
      // Call TTS API
      const response = await ttsApi.speak(
        text,
        options.language || language,
        options.voice || null,
        options.speed || 1.0
      );

      if (response.data.success && response.data.audio_base64) {
        // Create audio element
        const audio = new Audio(`data:audio/mp3;base64,${response.data.audio_base64}`);
        currentAudioRef.current = audio;

        audio.onplay = () => {
          setIsSpeaking(true);
          setIsLoading(false);
        };

        audio.onended = () => {
          setIsSpeaking(false);
          currentAudioRef.current = null;
        };

        audio.onerror = (e) => {
          console.error('Audio playback error:', e);
          setError('Failed to play audio');
          setIsSpeaking(false);
          setIsLoading(false);
        };

        // Play the audio
        await audio.play();
      } else {
        throw new Error(response.data.error || 'Failed to generate speech');
      }
    } catch (err) {
      console.error('TTS Error:', err);
      setError(err.message || 'Failed to generate speech');
      setIsLoading(false);
    }
  }, [language]);

  const stop = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
    setIsSpeaking(false);
    setIsLoading(false);
  }, []);

  const toggle = useCallback(async (text, options = {}) => {
    if (isSpeaking) {
      stop();
    } else {
      await speak(text, options);
    }
  }, [isSpeaking, speak, stop]);

  return {
    speak,
    stop,
    toggle,
    isSpeaking,
    isLoading,
    error,
    isSupported: true, // OpenAI TTS is always supported
  };
}

export default useOpenAITTS;
